import { initializeApp } from 'firebase/app';
import { connectFirestoreEmulator, getFirestore } from 'firebase/firestore';
import { connectAuthEmulator, getAuth } from 'firebase/auth';
import { connectFunctionsEmulator, getFunctions } from 'firebase/functions';
import { FirebaseRegions } from 'core/constants/firebase-regions';
import { connectStorageEmulator, getStorage } from 'firebase/storage';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_DOMAIN,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

const app = initializeApp(firebaseConfig);
export const firestore = getFirestore(app);
export const auth = getAuth(app);
export const functions = getFunctions(app, FirebaseRegions.EU_WEST_2);
export const storage = getStorage(app);

/**
 * Point parts of the app at the local emulator suite.
 *
 * `REACT_APP_USE_EMULATOR` in `portal/.env` takes either `true` (or `all`) for
 * the whole suite, or a comma-separated list of the services to emulate:
 *
 *   REACT_APP_USE_EMULATOR=functions          # local functions, real dev data
 *   REACT_APP_USE_EMULATOR=functions,auth
 *   REACT_APP_USE_EMULATOR=true               # everything, as the tests use
 *
 * The list matters for day-to-day work: emulating functions alone keeps you
 * signed in as your real account against real development data, while `true`
 * gives you an empty database and needs a seeded emulator user.
 *
 * This replaces a line that was commented in and out by hand, which could be
 * committed by accident and take a deployed build down — every callable would
 * try localhost:5001. Create React App inlines the flag at build time, so a
 * production build with it unset contains none of this.
 */
const EMULATABLE = ['auth', 'firestore', 'functions', 'storage'] as const;
type Emulatable = (typeof EMULATABLE)[number];

const requested = (process.env.REACT_APP_USE_EMULATOR || '').trim().toLowerCase();

const emulated: Emulatable[] =
  requested === 'true' || requested === 'all'
    ? [...EMULATABLE]
    : (requested
        .split(',')
        .map((name) => name.trim())
        .filter((name): name is Emulatable => (EMULATABLE as readonly string[]).includes(name)));

export const usingEmulators = emulated.length > 0;

if (usingEmulators) {
  const host = process.env.REACT_APP_EMULATOR_HOST || 'localhost';

  if (emulated.includes('auth')) connectAuthEmulator(auth, `http://${host}:9099`, { disableWarnings: true });
  if (emulated.includes('firestore')) connectFirestoreEmulator(firestore, host, 8080);
  if (emulated.includes('functions')) connectFunctionsEmulator(functions, host, 5001);
  if (emulated.includes('storage')) connectStorageEmulator(storage, host, 9199);

  // eslint-disable-next-line no-console
  console.warn(`[firebase] emulating ${emulated.join(', ')} on ${host}`);
}

export default app;
